/**
 * Self-Assign drawer — lets the current agent assign a conversation to themselves
 * by picking a Campaign and Team they belong to. Mirrors the KC2 self-assign
 * sidebar: only shows campaigns/teams the user is a member of, and teams are
 * filtered by the selected campaign.
 *
 * On submit, PATCHes the conversation with:
 *   { assignedAgentId, campaignId, assignedTeamId }
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { UserCheck } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useMyCampaignsTeams } from '../../../hooks/useMyCampaignsTeams.js'
import ActionDrawer from './ActionDrawer.js'
import { Field, GhostButton, PrimaryButton, SelectField } from './primitives.js'

export interface SelfAssignDrawerProps {
  open: boolean
  onClose: () => void
  /** The current agent's id (agents.id). */
  agentId: string | null
  /** Patch the conversation with assignment fields. */
  onAssign: (patch: {
    assignedAgentId: string
    campaignId: string
    assignedTeamId: string
  }) => Promise<boolean>
}

export default function SelfAssignDrawer({
  open,
  onClose,
  agentId: agentIdProp,
  onAssign,
}: SelfAssignDrawerProps): JSX.Element {
  const { campaigns, teams, loading } = useMyCampaignsTeams(open)
  const [campaignId, setCampaignId] = useState('')
  const [teamId, setTeamId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resolvedAgentId, setResolvedAgentId] = useState<string | null>(agentIdProp)

  // If the parent didn't resolve an agentId (e.g. /agent/me failed or was
  // slow), fetch it ourselves when the drawer opens so the submit button
  // can still activate.
  useEffect(() => {
    if (!open) return
    if (agentIdProp) { setResolvedAgentId(agentIdProp); return }
    setResolvedAgentId(null)
    api.get<{ id: string }>('/agent/me')
      .then((res) => setResolvedAgentId(res.id))
      .catch(() => setResolvedAgentId(null))
  }, [open, agentIdProp])

  const agentId = resolvedAgentId

  // Teams filtered by selected campaign (mirrors KC2 teams_items("self_assign"))
  const teamOptions = useMemo(
    () =>
      teams
        .filter((t) => !campaignId || t.campaignId === campaignId)
        .map((t) => ({ value: t.id, label: t.name })),
    [teams, campaignId],
  )

  const campaignOptions = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.name })),
    [campaigns],
  )

  const canSubmit = Boolean(campaignId && teamId && agentId)

  const submit = async (): Promise<void> => {
    if (!agentId || !campaignId || !teamId) return
    setSubmitting(true)
    const ok = await onAssign({
      assignedAgentId: agentId,
      campaignId,
      assignedTeamId: teamId,
    })
    setSubmitting(false)
    if (ok) {
      setCampaignId('')
      setTeamId('')
      onClose()
    }
  }

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Self Assign"
      subtitle="Assign this conversation to yourself"
      icon={<UserCheck className="h-4 w-4" />}
      info={{
        lead: 'Takes ownership of this conversation, filing it under one of your own campaigns and teams.',
        points: [
          { term: 'Campaign', detail: 'Only campaigns you belong to are listed.' },
          { term: 'Team', detail: 'Filtered by the campaign you picked — again, only teams you are a member of.' },
          { term: 'Effect', detail: 'You become the assigned agent; the conversation moves into your queue and out of whoever held it.' },
        ],
      }}
      width={480}
      footer={
        <>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={() => void submit()} disabled={!canSubmit || submitting || loading}>
            {submitting ? 'Assigning…' : 'Assign to me'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border border-slate-300 border-t-[#3366FF]" />
          </div>
        )}
        {!loading && campaigns.length === 0 && (
          <div className="rounded-[3px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            You are not assigned to any campaign. Contact your administrator to be added to a campaign and team.
          </div>
        )}
        {!loading && campaigns.length > 0 && (
          <>
            <Field label="Campaign" required>
              <SelectField
                value={campaignId}
                onChange={(v) => {
                  setCampaignId(v)
                  setTeamId('')
                }}
                placeholder="Select campaign"
                options={campaignOptions}
              />
            </Field>
            <Field label="Team" required>
              <SelectField
                value={teamId}
                onChange={setTeamId}
                placeholder={campaignId ? 'Select team' : 'Select a campaign first'}
                options={teamOptions}
                disabled={!campaignId}
              />
            </Field>
          </>
        )}
      </div>
    </ActionDrawer>
  )
}
